// src/components/ui.tsx
// Shared reusable UI primitives — liquid/motion-first revamp.
// Public API kept fully backwards compatible with original component shapes.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';

// ── Card ──────────────────────────────────────────────────────────
export function Card({ children, style, interactive, onClick }: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  interactive?: boolean;
  onClick?: (e?: any) => void;
}) {
  const base: React.CSSProperties = {
    background: 'var(--bg-card)',
    border: '1px solid var(--hairline)',
    borderRadius: 'var(--radius-md)',
    padding: 22,
    backdropFilter: 'saturate(125%) blur(20px)',
    WebkitBackdropFilter: 'saturate(125%) blur(20px)',
    boxShadow: 'none',
    transition: 'border-color var(--duration-fast), background var(--duration-fast)',
    ...style,
  };
  if (interactive || onClick) {
    return (
      <motion.div
        whileHover={{ borderColor: 'var(--border-strong)' as any, backgroundColor: 'var(--bg-elevated)' as any }}
        transition={{ duration: 0.22 }}
        onClick={onClick}
        style={base}
      >
        {children}
      </motion.div>
    );
  }
  return <div style={base} onClick={onClick}>{children}</div>;
}

// ── AnimatedNumber ─────────────────────────────────────────────────
function AnimatedNumber({ value }: { value: number | string }) {
  const numeric = typeof value === 'number' ? value : Number(String(value).replace(/[^\d.-]/g, ''));
  const valid = Number.isFinite(numeric);
  const mv = useMotionValue(0);
  const display = useTransform(mv, v => {
    if (!valid) return String(value);
    const isInt = Math.abs(numeric % 1) < 0.001;
    return isInt ? String(Math.round(v)) : v.toFixed(1);
  });
  useEffect(() => {
    if (!valid) return;
    const controls = animate(mv, numeric, { duration: 0.9, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
  }, [numeric, valid, mv]);
  if (!valid) return <>{value}</>;
  return <motion.span>{display}</motion.span>;
}

// ── StatBlock ─────────────────────────────────────────────────────
export function StatBlock({ label, value, sub, trend, color = 'green' }: {
  label: string; value: string | number; sub?: string; trend?: string; color?: 'green'|'gold'|'sky'|'red';
}) {
  const colors = {
    green: 'var(--brand-forest)',
    gold:  'var(--brand-champagne)',
    sky:   'var(--signal-info)',
    red:   'var(--signal-live)',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      whileHover={{ borderColor: 'var(--border-strong)' as any }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--hairline)',
        borderRadius: 'var(--radius-md)',
        padding: '20px 22px', position: 'relative',
        backdropFilter: 'saturate(125%) blur(20px)',
        WebkitBackdropFilter: 'saturate(125%) blur(20px)',
      }}
    >
      <div className="cl-eyebrow" style={{ marginBottom: 10 }}>{label}</div>
      <div className="cl-serif" style={{ fontSize: 38, fontWeight: 600, lineHeight: 1, color: 'var(--text-primary)', letterSpacing: -1, fontFeatureSettings: '"lnum","tnum"' }}>
        <AnimatedNumber value={value} />
      </div>
      {sub   && <div className="cl-mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, letterSpacing: 0.3 }}>{sub}</div>}
      {trend && <div className="cl-mono" style={{ fontSize: 11, fontWeight: 600, color: colors[color], marginTop: 8, letterSpacing: 0.3 }}>{trend}</div>}
    </motion.div>
  );
}

// ── Tag ───────────────────────────────────────────────────────────
export function Tag({ children, color = 'muted' }: { children: React.ReactNode; color?: 'green'|'gold'|'red'|'sky'|'muted' }) {
  const styles = {
    green: { background: 'transparent', color: 'var(--brand-forest)',   border: '1px solid var(--brand-forest)' },
    gold:  { background: 'transparent', color: 'var(--brand-champagne)', border: '1px solid var(--brand-champagne)' },
    red:   { background: 'transparent', color: 'var(--signal-live)',     border: '1px solid var(--signal-live)' },
    sky:   { background: 'transparent', color: 'var(--signal-info)',     border: '1px solid var(--signal-info)' },
    muted: { background: 'transparent', color: 'var(--text-muted)',      border: '1px solid var(--hairline)' },
  };
  return (
    <span className="cl-mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 'var(--radius-pill)', fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', ...styles[color] }}>
      {children}
    </span>
  );
}

// ── Button ────────────────────────────────────────────────────────
export function Btn({ children, onClick, variant = 'primary', size = 'md', fullWidth, disabled, style }: {
  children: React.ReactNode;
  onClick?: (e?: any) => void;
  variant?: 'primary'|'ghost'|'gold'|'red';
  size?: 'sm'|'md';
  fullWidth?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    fontFamily: 'var(--font-body)', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none', whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1,
    width: fullWidth ? '100%' : undefined,
    padding: size === 'sm' ? '6px 12px' : '10px 18px',
    fontSize: size === 'sm' ? 12.5 : 13.5,
    borderRadius: size === 'sm' ? 9 : 12,
    letterSpacing: 0.1,
    position: 'relative', overflow: 'hidden',
  };
  const variants = {
    primary: { background: 'var(--brand-ink)', color: 'var(--brand-bone)', border: '1px solid var(--brand-champagne)', boxShadow: 'none' as const },
    ghost:   { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--hairline)', boxShadow: 'none' as const },
    gold:    { background: 'var(--brand-champagne)', color: 'var(--brand-ink)', border: '1px solid var(--brand-brass)', boxShadow: 'none' as const },
    red:     { background: 'transparent', color: 'var(--signal-live)', border: '1px solid var(--signal-live)', boxShadow: 'none' as const },
  };
  return (
    <motion.button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      whileHover={disabled ? undefined : { y: -1, scale: 1.015 }}
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 420, damping: 24 }}
      style={{ ...base, ...variants[variant], ...style }}
    >
      {children}
    </motion.button>
  );
}

// ── Avatar ────────────────────────────────────────────────────────
export function Avatar({ initials, color = '#0D5C2E', size = 36, rounded = true }: {
  initials: string; color?: string; size?: number; rounded?: boolean;
}) {
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      borderRadius: rounded ? '50%' : size > 40 ? 12 : 8,
      background: `linear-gradient(135deg, ${color}, ${color}cc 60%, ${color}88)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size > 40 ? 18 : 12, fontWeight: 700, color: '#fff',
      boxShadow: `0 4px 14px ${color}44, 0 1px 0 rgba(255,255,255,0.2) inset`,
      letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
}

// ── OverBall ──────────────────────────────────────────────────────
export function OverBall({ val }: { val: string }) {
  const styles: Record<string, React.CSSProperties> = {
    '0': { background: 'var(--bg-surface)', color: 'var(--text-muted)' },
    '1': { background: 'rgba(34,197,94,0.10)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' },
    '2': { background: 'rgba(34,197,94,0.14)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' },
    '3': { background: 'rgba(34,197,94,0.18)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' },
    '4': { background: 'rgba(14,165,233,0.14)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.40)' },
    '6': { background: 'rgba(212,160,23,0.16)', color: '#D4A017', border: '1px solid rgba(212,160,23,0.40)' },
    'W': { background: 'rgba(220,38,38,0.14)', color: '#F87171', border: '1px solid rgba(220,38,38,0.40)' },
    'wd': { background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' },
    'nb': { background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' },
  };
  return (
    <motion.div
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      style={{
        width: 28, height: 28, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontSize: val === 'wd' || val === 'nb' ? 9 : 11,
        fontWeight: 700, flexShrink: 0,
        ...(styles[val] || styles['1']),
      }}
    >
      {val}
    </motion.div>
  );
}

// ── LoadingSpinner ─────────────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        border: '3px solid var(--border-color)', borderTopColor: 'var(--brand-grass)',
        borderRightColor: 'var(--brand-gold)',
        animation: 'spin 0.9s linear infinite',
      }} />
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────
// ── NoAccess ───────────────────────────────────────────────────────
// Use in place of hiding/disabling a feature when the signed-in user
// lacks the role to use it. Shows what's locked, who can do it, and an
// optional action.
export function NoAccess({
  title = 'You don\'t have access to this',
  needs,
  desc,
  action,
}: {
  title?: string;
  needs?: string;
  desc?: string;
  action?: React.ReactNode;
}) {
  return (
    <div style={{
      textAlign: 'center', padding: '40px 24px',
      border: '1px dashed var(--hairline)',
      borderRadius: 'var(--radius-md)',
      background: 'var(--bg-surface)',
    }}>
      <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.55 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: 'var(--text-primary)' }}>{title}</div>
      {needs && (
        <div style={{ fontSize: 12, color: 'var(--brand-champagne)', marginBottom: 8, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
          Needs · {needs}
        </div>
      )}
      {desc && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, maxWidth: 420, margin: '0 auto 16px' }}>{desc}</div>}
      {action}
    </div>
  );
}

export function Empty({ icon = '🏏', title, desc, action }: {
  icon?: string; title: string; desc?: string; action?: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{ textAlign: 'center', padding: '64px 24px' }}
    >
      <div style={{ fontSize: 56, marginBottom: 14, opacity: 0.5, filter: 'saturate(80%)' }}>{icon}</div>
      <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>{title}</div>
      {desc && <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 18, maxWidth: 380, margin: '0 auto 18px' }}>{desc}</div>}
      {action}
    </motion.div>
  );
}

// ── DataTable ─────────────────────────────────────────────────────
export function DataTable({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {headers.map(h => (
            <th key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1.2, textTransform: 'uppercase', padding: '12px 12px', borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} style={{ borderBottom: ri < rows.length - 1 ? '1px solid var(--border-subtle)' : 'none', transition: 'background var(--duration-fast)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
            {row.map((cell, ci) => (
              <td key={ci} style={{ padding: '13px 12px', fontSize: 13.5, verticalAlign: 'middle', color: 'var(--text-primary)' }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onChange }: { tabs: string[]; active: string; onChange: (t: string) => void }) {
  return (
    <div className="cl-tabs" style={{
      display: 'flex', gap: 2, padding: 4, marginBottom: 20,
      background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border-subtle)',
    }}>
      {tabs.map(t => {
        const isActive = active === t;
        return (
          <button key={t} onClick={() => onChange(t)} style={{
            flex: 1, padding: '8px 14px', borderRadius: 9, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', border: 'none', fontFamily: 'var(--font-body)', textAlign: 'center',
            background: 'transparent', color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
            position: 'relative', zIndex: 1, transition: 'color var(--duration-fast)',
          }}>
            {isActive && (
              <motion.div
                layoutId="tab-pill"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                style={{
                  position: 'absolute', inset: 0, zIndex: -1, borderRadius: 9,
                  background: 'var(--bg-card-solid)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18), 0 1px 0 var(--glass-highlight) inset',
                  border: '1px solid var(--border-color)',
                }}
              />
            )}
            {t}
          </button>
        );
      })}
    </div>
  );
}

// ── ProgressBar ───────────────────────────────────────────────────
export function ProgressBar({ value, max = 100, color = '#22C55E' }: { value: number; max?: number; color?: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div style={{ height: 6, background: 'var(--bg-surface)', borderRadius: 999, overflow: 'hidden' }}>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        style={{ height: '100%', background: `linear-gradient(90deg, ${color}aa, ${color})`, borderRadius: 999 }}
      />
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, subtitle, children }: {
  open: boolean; onClose: () => void; title: string; subtitle?: string; children: React.ReactNode;
}) {
  // Render conditionally with NO AnimatePresence. framer-motion's exit
  // animation kept the full-screen backdrop (position:fixed inset:0
  // z-index:1000 pointer-events:auto backdrop-filter:blur(8px)) mounted
  // for ~250ms after `open` flipped false, blocking taps on whatever
  // page was visible underneath. Instant unmount removes the dead
  // backdrop the moment the modal closes. The inner card still uses a
  // motion.div for the entrance pop-in.
  const inner = open ? (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card-solid)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)',
          padding: 30, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 40px 120px rgba(0,0,0,0.45), 0 1px 0 var(--glass-highlight) inset',
        }}
      >
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, letterSpacing: -0.5, marginBottom: subtitle ? 4 : 20 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 20 }}>{subtitle}</div>}
        {children}
      </motion.div>
    </div>
  ) : null;
  if (typeof document === 'undefined') return inner;
  return createPortal(inner, document.body);
}

// ── FormInput ─────────────────────────────────────────────────────
export function FormInput({ label, type = 'text', value, onChange, placeholder }: {
  label: string; type?: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 7 }}>
        {label}
      </label>
      <input
        type={type} value={value} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', background: 'var(--bg-surface)',
          border: `1px solid ${focused ? 'var(--brand-grass)' : 'var(--border-color)'}`,
          borderRadius: 'var(--radius-sm)', padding: '11px 14px', fontSize: 15,
          color: 'var(--text-primary)', fontFamily: 'var(--font-body)', outline: 'none',
          boxSizing: 'border-box', WebkitAppearance: 'none',
          boxShadow: focused ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
          transition: 'border-color var(--duration-fast), box-shadow var(--duration-fast)',
        }}
      />
    </div>
  );
}

export function LoadingScreen() {
  // CSS-only fade. Avoids importing framer-motion into the Suspense fallback
  // path which is what users see during the cold first paint on mobile.
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'transparent', flexDirection: 'column', gap: 18, animation: 'fadeIn 250ms ease' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 900, letterSpacing: -1 }}>
        <span className="cl-grad-text">CricLocal</span>
      </div>
      <Spinner />
    </div>
  );
}

// ── Social Media Icons ───────────────────────────────────────
const SOCIAL_PLATFORMS: { key: string; label: string; icon: string; prefix: string }[] = [
  { key: 'facebook', label: 'Facebook', icon: 'f', prefix: 'https://facebook.com/' },
  { key: 'instagram', label: 'Instagram', icon: '📷', prefix: 'https://instagram.com/' },
  { key: 'twitter', label: 'X / Twitter', icon: '𝕏', prefix: 'https://x.com/' },
  { key: 'youtube', label: 'YouTube', icon: '▶', prefix: 'https://youtube.com/' },
  { key: 'tiktok', label: 'TikTok', icon: '♪', prefix: 'https://tiktok.com/@' },
  { key: 'website', label: 'Website', icon: '🌐', prefix: '' },
];

export function SocialIcons({ links, editable, onEdit }: { links: Record<string, string>; editable?: boolean; onEdit?: () => void }) {
  const active = SOCIAL_PLATFORMS.filter(p => links[p.key]);
  if (active.length === 0 && !editable) return null;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      {active.map(p => (
        <a key={p.key} href={links[p.key]} target="_blank" rel="noopener noreferrer" title={p.label}
          style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--brand-grass)', textDecoration: 'none', border: '1px solid var(--border-color)' }}>
          {p.icon}
        </a>
      ))}
      {editable && <button onClick={onEdit} style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--bg-surface)', border: '1px solid var(--border-color)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Edit social links">✎</button>}
    </div>
  );
}

export { SOCIAL_PLATFORMS };

// ── Contact Cards ────────────────────────────────────────────
export function ContactCards({ contacts, editable, onAdd, onDelete }: {
  contacts: { id: string; contactName: string; role?: string; phone?: string; isWhatsapp: boolean }[];
  editable?: boolean;
  onAdd?: () => void;
  onDelete?: (id: string) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Key Contacts</div>
        {editable && <Btn size="sm" onClick={onAdd}>+ Add Contact</Btn>}
      </div>
      {contacts.length === 0 ? <div style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>No contacts added yet</div> :
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {contacts.map(c => {
            const cleanPhone = c.phone?.replace(/[^0-9+]/g, '') || '';
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#0D5C2E,#22C55E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0, color: '#fff' }}>
                  {c.contactName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.contactName}</div>
                  {c.role && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.role}</div>}
                </div>
                {c.phone && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={`tel:${cleanPhone}`} style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--bg-card-solid)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, textDecoration: 'none', border: '1px solid var(--border-color)' }} title="Call">📞</a>
                    {c.isWhatsapp && <a href={`https://wa.me/${cleanPhone.replace('+', '')}`} target="_blank" rel="noopener noreferrer" style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(37,211,102,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, textDecoration: 'none', border: '1px solid rgba(37,211,102,0.30)' }} title="WhatsApp">💬</a>}
                  </div>
                )}
                {editable && onDelete && <button onClick={() => onDelete(c.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }} title="Remove">✕</button>}
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}

// ── Photo Gallery ────────────────────────────────────────────
export function PhotoGallery({ photos, canUpload, canDelete, onUpload, onDelete, uploading }: {
  photos: { id: string; imageUrl: string; caption?: string }[];
  canUpload?: boolean;
  canDelete?: boolean;
  onUpload?: (file: File) => void;
  onDelete?: (photoId: string) => void;
  uploading?: boolean;
}) {
  const [lightbox, setLightbox] = React.useState<string | null>(null);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Photo Gallery</div>
        {canUpload && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 10, background: 'linear-gradient(135deg,#0D5C2E,#22C55E)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1, boxShadow: '0 4px 14px rgba(34,197,94,0.30)' }}>
            {uploading ? 'Uploading...' : '+ Upload Photo'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0] && onUpload) onUpload(e.target.files[0]); e.target.value = ''; }} disabled={uploading} />
          </label>
        )}
      </div>
      {photos.length === 0 ? <div style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>No photos yet</div> :
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          {photos.map(p => (
            <motion.div key={p.id} whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              style={{ position: 'relative', borderRadius: 'var(--radius-sm)', overflow: 'hidden', aspectRatio: '1', background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
              <img src={p.imageUrl} alt={p.caption || ''} onClick={() => setLightbox(p.imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer', display: 'block' }} />
              {canDelete && onDelete && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                  style={{ position: 'absolute', top: 5, right: 5, width: 26, height: 26, borderRadius: '50%', background: 'rgba(220,38,38,0.92)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
                  title="Delete photo">✕</button>
              )}
            </motion.div>
          ))}
        </div>
      }
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', backdropFilter: 'blur(8px)' }}
          >
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              src={lightbox} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 14 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Share Menu ───────────────────────────────────────────────
export function ShareMenu({ url, text, subject }: { url: string; text: string; subject?: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareOptions = [
    { label: 'WhatsApp', icon: '💬', href: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}` },
    { label: 'Facebook', icon: 'f', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { label: 'X / Twitter', icon: '𝕏', href: `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
    { label: 'Email', icon: '✉', href: `mailto:?subject=${encodeURIComponent(subject || text)}&body=${encodeURIComponent(text + '\n' + url)}` },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Btn size="sm" variant="ghost" onClick={() => setOpen(!open)}>↗ Share</Btn>
      <AnimatePresence>
        {open && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.96 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 1000,
                background: 'var(--bg-card-solid)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)',
                padding: 8, minWidth: 200, boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
              }}
            >
              {shareOptions.map(opt => (
                <a key={opt.label} href={opt.href} target="_blank" rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                    borderRadius: 9, fontSize: 13.5, fontWeight: 500, color: 'var(--text-primary)',
                    textDecoration: 'none', cursor: 'pointer',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <span style={{ width: 22, textAlign: 'center' }}>{opt.icon}</span>
                  {opt.label}
                </a>
              ))}
              <button onClick={() => { copyLink(); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                  borderRadius: 9, fontSize: 13.5, fontWeight: 500, color: 'var(--text-primary)',
                  background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
                  fontFamily: 'var(--font-body)',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span style={{ width: 22, textAlign: 'center' }}>{copied ? '✓' : '🔗'}</span>
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Export Buttons ───────────────────────────────────────────
export function ExportButtons({ onCSV, onPrint }: { onCSV?: () => void; onPrint?: () => void }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {onCSV && <Btn size="sm" variant="ghost" onClick={onCSV}>📊 Export CSV</Btn>}
      {onPrint && <Btn size="sm" variant="ghost" onClick={onPrint}>🖨 Print / PDF</Btn>}
    </div>
  );
}

// ── CSV Download Helper ─────────────────────────────────────
export function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const csv = [
    headers.join(','),
    ...rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename + '.csv';
  a.click();
  URL.revokeObjectURL(blobUrl);
}
